import { Badge, HStack, Icon, Tooltip, Text, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { TbX } from 'react-icons/tb';

interface OrderTagProps {
    tag: 'potential-total' | 'oem-endorsement' | 'dealer-referral' | 'quotes-only' | 'archived' | 'completed' | 'oem-cetification' | 'manually-flagged' | 'reviewed-by';
    text: string;
    onClick?: () => void;
    isPending?: boolean;
    tooltip?: string;
    isDisabled?: boolean;
}

const OrderTag = ({ tag, text, onClick, isPending = false, tooltip, isDisabled = false }: OrderTagProps) => {
    const isReviewedTag = tag === 'reviewed-by';

    const colorScheme = useMemo(() => {
        switch (tag) {
            case 'dealer-referral': return 'cyan';
            case 'oem-endorsement': return 'blue';
            case 'potential-total': return 'yellow';
            case 'quotes-only': return 'teal';
            case 'archived': return 'gray';
            case 'completed': return 'green';
            case 'oem-cetification': return 'purple';
            case 'manually-flagged': return 'orange';
            case 'reviewed-by': return 'yellow';
            default: return 'gray';
        }
    }, [tag]);

    const badge = (
        <Badge
            colorScheme={colorScheme}
            variant="subtle"
            bg={isReviewedTag ? '#E9E1F5' : undefined}
            color={isReviewedTag ? '#3F315F' : undefined}
            px={2}
            py={1}
            fontSize="xs"
            fontWeight="medium"
            borderRadius="md"
            cursor={onClick && !isDisabled ? "pointer" : isDisabled ? "not-allowed" : "default"}
            onClick={isDisabled ? undefined : onClick}
            _hover={onClick && !isDisabled ? {
                bg: isReviewedTag ? '#DDD1F0' : `${colorScheme}.100`,
                color: isReviewedTag ? '#31284D' : `${colorScheme}.700`,
                transform: "translateY(-1px)"
            } : undefined}
            transition="all 0.2s"
            opacity={isPending || isDisabled ? 0.7 : 1}
        >
            <HStack spacing={1} align="center">
                <span>{text}</span>
                {onClick && !isPending && !isDisabled && (
                    <Icon
                        as={TbX as React.ElementType}
                        w={3}
                        h={3}
                        cursor="pointer"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                        }}
                        _hover={{
                            opacity: 0.7,
                            transform: 'scale(1.2)'
                        }}
                        transition="all 0.2s"
                    />
                )}
            </HStack>
        </Badge>
    );

    if (tooltip) {
        // Parse tooltip string to extract lines if it contains \n
        const tooltipLines = tooltip.split('\n');
        const tooltipContent = tooltipLines.length > 1 ? (
            <VStack align="start" spacing={0}>
                {tooltipLines.map((line, index) => (
                    <Text key={index} fontSize="sm">{line}</Text>
                ))}
            </VStack>
        ) : tooltip;

        return (
            <Tooltip label={tooltipContent} placement="top" hasArrow>
                {badge}
            </Tooltip>
        );
    }

    return badge;
};

export default OrderTag;